import React, { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  meetingService,
  contextService,
  assistantService,
  agentService,
} from "../../services/api";
import { newRequestId } from "../../utils/requestId";
import MeetingFollowups from "./MeetingFollowups";
import MeetingRecording from "./MeetingRecording";

const field = "block text-xs font-semibold text-[#58635d]";
const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const time = (value, timezone) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        timeZone: timezone,
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Deadline to confirm";
const localInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

function useRequest() {
  const previous = useRef(null);
  const request = (payload) => {
    const serialized = JSON.stringify(payload);
    if (previous.current?.serialized !== serialized)
      previous.current = { serialized, id: newRequestId() };
    return { ...payload, request_id: previous.current.id };
  };
  return [
    request,
    () => {
      previous.current = null;
    },
  ];
}

function WorkspaceForm({ data, busy, save, close }) {
  const [values, setValues] = useState({
    ...data.workspace,
    expected_revision: data.revision,
  });
  const { data: contexts, error } = useQuery({
    queryKey: ["contexts", "meeting-options"],
    queryFn: () => contextService.getContexts({ status: "all" }),
  });
  return (
    <form
      className="surface-panel space-y-3 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(values);
      }}
    >
      <h2 className="font-bold">Agenda, decisions and continuity</h2>
      <label className={field}>
        Shared context
        <select
          className={input}
          value={values.context_id || ""}
          onChange={(e) =>
            setValues({
              ...values,
              context_id: e.target.value ? Number(e.target.value) : null,
            })
          }
        >
          <option value="">No shared context</option>
          {contexts?.contexts.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
      </label>
      {error && (
        <p role="alert" className="text-sm">
          Context options could not load. Retry after closing this form.
        </p>
      )}
      <p className="text-xs text-[#68746d]">
        Use the same context for related meetings to bring forward unfinished
        commitments.
      </p>
      <label className={field}>
        Agenda
        <textarea
          className={input}
          rows={3}
          maxLength={10000}
          value={values.agenda}
          onChange={(e) => setValues({ ...values, agenda: e.target.value })}
        />
      </label>
      <label className={field}>
        Decisions / outcome
        <textarea
          className={input}
          rows={3}
          maxLength={10000}
          value={values.outcome}
          onChange={(e) => setValues({ ...values, outcome: e.target.value })}
        />
      </label>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          Save meeting details
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function MeetingActionForm({
  initial,
  busy,
  save,
  close,
  button = "Track action",
}) {
  const [values, setValues] = useState(
    initial || {
      title: "",
      description: "",
      owner: "",
      priority: "normal",
      due_at: null,
      status: "open",
      domain: "work",
      kind: "task",
      recurrence: "none",
    },
  );
  const [due, setDue] = useState(localInput(initial?.due_at));
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="surface-panel space-y-3 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        save({ ...values, due_at: due ? new Date(due).toISOString() : null });
      }}
    >
      <label className={field}>
        Action title
        <input
          required
          className={input}
          maxLength={240}
          value={values.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </label>
      <label className={field}>
        Owner
        <input
          className={input}
          maxLength={240}
          placeholder="Leave blank if not agreed"
          value={values.owner}
          onChange={(e) => set("owner", e.target.value)}
        />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={field}>
          Deadline (your browser timezone)
          <input
            type="datetime-local"
            className={input}
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </label>
        <label className={field}>
          Priority
          <select
            className={input}
            value={values.priority}
            onChange={(e) => set("priority", e.target.value)}
          >
            {["critical", "high", "normal", "low"].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
      </div>
      <label className={field}>
        Action status
        <select
          className={input}
          value={values.status}
          onChange={(e) => set("status", e.target.value)}
        >
          {["open", "waiting", "blocked"].map((item) => (
            <option key={item}>{item}</option>
          ))}
        </select>
      </label>
      <label className={field}>
        Action details
        <textarea
          className={input}
          rows={2}
          maxLength={10000}
          value={values.description}
          onChange={(e) => set("description", e.target.value)}
        />
      </label>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          {button}
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function SourcePicker({ eventId, linked, busy, attach, close }) {
  const [kind, setKind] = useState("note");
  const [text, setText] = useState("");
  const [offset, setOffset] = useState(0);
  const { data, error, isLoading } = useQuery({
    queryKey: ["meeting-source-options", eventId, kind, text, offset],
    queryFn: () =>
      meetingService.sourceOptions(eventId, { kind, text, offset }),
  });
  return (
    <section
      className="surface-panel space-y-3 p-4"
      aria-label="Choose a meeting source"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={field}>
          Source type
          <select
            className={input}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setOffset(0);
            }}
          >
            <option value="note">Saved notes</option>
            <option value="source">Connected text sources</option>
          </select>
        </label>
        <label className={field}>
          Find source by title
          <input
            className={input}
            maxLength={180}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOffset(0);
            }}
          />
        </label>
      </div>
      {isLoading && <p role="status">Loading sources…</p>}
      {error && <p role="alert">Sources could not load.</p>}
      {data?.items.map((item) => (
        <div
          key={item.input_key}
          className="flex items-center justify-between gap-3 text-sm"
        >
          <span>{item.title}</span>
          <button
            className="secondary-button text-xs"
            disabled={busy || linked.includes(item.input_key)}
            onClick={() => attach(item.input_key)}
          >
            {linked.includes(item.input_key) ? "Linked" : "Link source"}
          </button>
        </div>
      ))}
      {data && !data.items.length && (
        <p className="text-sm text-[#68746d]">
          No available text sources on this page. Save a note or sync a document
          in Connections.
        </p>
      )}
      <div className="flex gap-2">
        {offset > 0 && (
          <button
            className="secondary-button text-xs"
            onClick={() => setOffset(Math.max(0, offset - 30))}
          >
            Previous sources
          </button>
        )}
        {data?.next_offset != null && (
          <button
            className="secondary-button text-xs"
            onClick={() => setOffset(data.next_offset)}
          >
            More sources
          </button>
        )}
        <button className="quiet-button text-xs" onClick={close}>
          Close source picker
        </button>
      </div>
    </section>
  );
}

function FollowupForm({ initial, busy, save, close }) {
  const [values, setValues] = useState(initial);
  const [recipients, setRecipients] = useState("");
  return (
    <form
      className="surface-panel space-y-3 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save({
          ...values,
          recipients: recipients
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
        });
      }}
    >
      <h2 className="font-bold">Review follow-up draft</h2>
      <p className="text-sm text-[#68746d]">
        Choose recipients explicitly. Saving creates an unsent draft in Outbox.
      </p>
      <label className={field}>
        Recipients (comma separated emails)
        <input
          required
          className={input}
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
        />
      </label>
      <label className={field}>
        Subject
        <input
          required
          maxLength={240}
          className={input}
          value={values.subject}
          onChange={(e) => setValues({ ...values, subject: e.target.value })}
        />
      </label>
      <label className={field}>
        Follow-up message
        <textarea
          required
          rows={10}
          maxLength={20000}
          className={input}
          value={values.body}
          onChange={(e) => setValues({ ...values, body: e.target.value })}
        />
      </label>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          Save unsent follow-up
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ActionCard({ item, timezone, busy, run, edit }) {
  return (
    <article className="surface-panel space-y-2 p-4">
      <div className="flex justify-between gap-2">
        <h3 className="font-bold">{item.title}</h3>
        <span className="status-pill">
          {item.priority} · {item.status}
        </span>
      </div>
      <p className="text-sm text-[#58635d]">
        {item.owner || "Owner to confirm"} · {time(item.due_at, timezone)}
      </p>
      {item.description && (
        <p className="whitespace-pre-wrap text-sm text-[#68746d]">
          {item.description}
        </p>
      )}
      {item.attention && (
        <p className="text-sm text-[#996617]">
          {item.attention.replaceAll("_", " ")}: {item.reason}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          disabled={busy}
          className="secondary-button text-xs"
          onClick={() =>
            run(() =>
              assistantService.state(item.id, {
                action:
                  item.status === "done" || item.status === "cancelled"
                    ? "reopen"
                    : "complete",
                expected_due_at: item.due_at,
              }),
            )
          }
        >
          {item.status === "done" || item.status === "cancelled"
            ? "Reopen action"
            : "Mark action complete"}
        </button>
        {!["done", "cancelled"].includes(item.status) && (
          <button className="quiet-button text-xs" onClick={() => edit(item)}>
            Edit action
          </button>
        )}
      </div>
    </article>
  );
}

export default function MeetingPage() {
  const { eventId } = useParams();
  return <MeetingWorkspace key={eventId} eventId={eventId} />;
}

export function MeetingWorkspace({ eventId }) {
  const client = useQueryClient();
  const [offset, setOffset] = useState(0);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["meeting", String(eventId), offset],
    queryFn: () => meetingService.get(eventId, { offset }),
    refetchInterval: 15000,
  });
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(false);
  const [picker, setPicker] = useState(false);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState("");
  const [followup, setFollowup] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [noteRequest, resetNoteRequest] = useRequest();
  const [actionRequest, resetActionRequest] = useRequest();
  const [draftRequest, resetDraftRequest] = useRequest();
  const run = async (operation) => {
    if (busy) return false;
    setBusy(true);
    setFailure("");
    setNotice("");
    try {
      await operation();
      await Promise.all(
        [
          "meeting",
          "assistant",
          "agent",
          "today",
          "contexts",
          "context",
          "notes",
          "record-proposals",
        ].map((key) => client.invalidateQueries({ queryKey: [key] })),
      );
      return true;
    } catch (reason) {
      if (reason.response?.status === 409) await client.invalidateQueries({ queryKey: ["meeting", String(eventId)] });
      const detail = reason.response?.data?.detail;
      setFailure(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((item) => item.msg).join("; ")
            : "Could not save the meeting change. Retry with the same details.",
      );
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (isLoading)
    return (
      <div className="app-page">
        <p role="status">Loading meeting workspace…</p>
      </div>
    );
  if (error || !data)
    return (
      <div className="app-page">
        <p role="alert">Meeting unavailable.</p>
        <button className="secondary-button" onClick={() => refetch()}>
          Retry meeting
        </button>
      </div>
    );
  const ended = new Date(data.event.end_time) <= new Date();
  const analysing = data.analysis.some((item) => item.status === "running");
  return (
    <main className="app-page">
      <div className="app-page-inner max-w-6xl space-y-5">
        <Link to="/calendar" className="text-sm font-semibold text-[#246b52]">
          ← Calendar
        </Link>
        <header className="page-header">
          <div>
            <p className="eyebrow">Meeting follow-through</p>
            <h1 className="page-title">{data.event.title}</h1>
            {data.event.connected && (
              <Link
                className="text-sm font-semibold text-[#246b52] underline"
                to={`/calendar/events/${eventId}`}
              >
                Google Calendar changes
              </Link>
            )}
            <p className="page-description">
              {time(data.event.start_time, data.timezone)} –{" "}
              {time(data.event.end_time, data.timezone)} · {data.timezone}
            </p>
            {data.event.location && (
              <p className="mt-2 text-sm">{data.event.location}</p>
            )}
          </div>
          <button className="secondary-button" onClick={() => setEditing(true)}>
            Edit agenda and outcome
          </button>
        </header>
        {data.event.calendar_refresh_pending && (
          <p role="status" className="surface-panel p-4">
            These are the last known meeting times. The series changed and this occurrence needs refresh in Google Calendar changes. Automatic attendance reminders and follow-up drafts are paused. Your notes and agreed actions remain available.
          </p>
        )}
        {data.event.status === "cancelled" && (
          <p role="status" className="surface-panel p-4">
            This meeting is cancelled. Previously tracked commitments remain
            available for review.
          </p>
        )}
        {data.event.response_status === "declined" && data.event.status !== "cancelled" && (
          <p role="status" className="surface-panel p-4">
            This invitation is declined. Automatic meeting reminders are paused; notes and agreed actions remain available.
          </p>
        )}
        {failure && (
          <p role="alert" className="surface-panel p-4 text-[#a14d38]">
            {failure}
          </p>
        )}
        {notice && (
          <p role="status" className="surface-panel p-4 text-[#246b52]">
            {notice}
          </p>
        )}
        {editing ? (
          <WorkspaceForm
            data={data}
            busy={busy}
            close={() => setEditing(false)}
            save={(values) =>
              run(async () => {
                await meetingService.configure(eventId, values);
                setEditing(false);
              })
            }
          />
        ) : (
          <section className="surface-panel grid gap-5 p-5 md:grid-cols-2">
            <div>
              <h2 className="font-bold">Agenda</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm text-[#68746d]">
                {data.workspace.agenda || "No agenda recorded."}
              </p>
            </div>
            <div>
              <h2 className="font-bold">Decisions / outcome</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm text-[#68746d]">
                {data.workspace.outcome || "No outcome recorded yet."}
              </p>
            </div>
            {data.workspace.context_id && (
              <Link
                className="text-sm font-semibold text-[#246b52] underline"
                to={`/contexts/${data.workspace.context_id}`}
              >
                Open shared context
              </Link>
            )}
          </section>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <span className="status-pill">
            {data.prepared
              ? "Preparation reviewed"
              : "Preparation not confirmed"}
          </span>
          <span className="status-pill">
            {data.reviewed
              ? "Notes and actions reviewed"
              : "Notes review outstanding"}
          </span>
          {!data.prepared && !ended && data.event.status !== "cancelled" && data.event.response_status !== "declined" && (
            <button
              disabled={busy}
              className="secondary-button text-xs"
              onClick={() =>
                run(() =>
                  meetingService.review(eventId, {
                    action: "prepared",
                    expected_revision: data.prepared_revision,
                  }),
                )
              }
            >
              Mark prepared
            </button>
          )}
          <button
            disabled={
              busy ||
              (!data.reviewed &&
                (!ended || analysing || data.transcriptions_pending || !!data.proposals.length))
            }
            className="secondary-button text-xs"
            onClick={() =>
              run(() =>
                meetingService.review(eventId, {
                  action: data.reviewed ? "reopen" : "reviewed",
                  expected_revision: data.review_revision,
                }),
              )
            }
          >
            {data.reviewed ? "Reopen notes review" : "Finish notes review"}
          </button>
          <button
            disabled={busy}
            className="primary-button text-xs"
            onClick={() =>
              run(async () => {
                setFollowup(await meetingService.followup(eventId));
                setDraftSaved(false);
              })
            }
          >
            Prepare follow-up draft
          </button>
        </div>
        <p className="text-xs text-[#68746d]">
          Finish notes review after the meeting and after deciding any extracted
          action proposals. Tracked commitments stay open until you explicitly
          complete them.
        </p>
        {followup && (
          <FollowupForm
            key={followup.expected_revision}
            initial={followup}
            busy={busy}
            close={() => setFollowup(null)}
            save={(values) =>
              run(async () => {
                await meetingService.draftFollowup(
                  eventId,
                  draftRequest(values),
                );
                resetDraftRequest();
                setFollowup(null);
                setDraftSaved(true);
              })
            }
          />
        )}
        {draftSaved && (
          <p role="status" className="surface-panel p-4 text-sm text-[#246b52]">
            Follow-up saved as an unsent draft.{" "}
            <Link
              className="font-semibold underline"
              to="/assistant?view=outbox"
            >
              Review in Outbox
            </Link>
          </p>
        )}
        <MeetingFollowups data={data.followup} busy={busy}
          save={values => run(() => meetingService.followupPolicy(eventId, values))} />
        {data.carryover.length > 0 && (
          <section
            className="space-y-3"
            aria-label="Unfinished commitments from previous meetings"
          >
            <h2 className="text-lg font-bold">
              Bring forward · {data.carryover.length} unfinished commitments
            </h2>
            <p className="text-sm text-[#68746d]">
              Open work from earlier meetings in the same context.
            </p>
            {data.carryover.map((item) => (
              <ActionCard
                key={item.id}
                item={item}
                timezone={data.timezone}
                busy={busy}
                run={run}
                edit={(item) => setAction({ type: "edit", item })}
              />
            ))}
          </section>
        )}
        <section className="space-y-3" aria-label="Meeting notes and sources">
          <MeetingRecording eventId={eventId} />
          <div className="flex flex-wrap justify-between gap-3">
            <h2 className="text-lg font-bold">Notes and source evidence</h2>
            <button
              className="secondary-button text-xs"
              onClick={() => setPicker(true)}
            >
              Link saved source
            </button>
          </div>
          {picker && (
            <SourcePicker
              eventId={eventId}
              linked={data.sources.map((item) => item.input_key)}
              busy={busy}
              close={() => setPicker(false)}
              attach={(input_key) =>
                run(() => meetingService.linkSource(eventId, { input_key }))
              }
            />
          )}
          <form
            className="surface-panel space-y-3 p-4"
            onSubmit={(event) => {
              event.preventDefault();
              run(async () => {
                await meetingService.capture(
                  eventId,
                  noteRequest({ content: note }),
                );
                resetNoteRequest();
                setNote("");
                setNotice(
                  "Meeting notes saved. Analyse the source below to find supported action items.",
                );
              });
            }}
          >
            <label className={field}>
              Capture meeting notes
              <textarea
                required
                maxLength={30000}
                rows={4}
                className={input}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Decisions, agreed owners, deadlines, open questions and what needs follow-up. Use explicit dates where known."
              />
            </label>
            <button
              className="primary-button text-xs"
              disabled={busy || !note.trim()}
            >
              Save meeting notes
            </button>
          </form>
          {data.sources.map((source) => (
            <article
              key={source.input_key}
              className="surface-panel space-y-3 p-4"
            >
              <h3 className="font-bold">{source.title}</h3>
              {source.available ? (
                <details>
                  <summary className="cursor-pointer text-xs font-semibold">
                    Read source text
                  </summary>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-[#58635d]">
                    {source.text}
                  </p>
                </details>
              ) : (
                <p className="text-sm text-[#996617]">
                  The source was removed, archived or is not synced. Existing
                  commitments remain tracked.
                </p>
              )}
              {/^https?:\/\//i.test(source.url || "") && (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs underline"
                >
                  Open source
                </a>
              )}
              <div className="flex gap-2">
                <button
                  className="secondary-button text-xs"
                  disabled={busy || !source.available || analysing}
                  onClick={() =>
                    run(async () => {
                      const result = await meetingService.analyse(eventId, {
                        input_key: source.input_key,
                      });
                      setNotice(result.message);
                    })
                  }
                >
                  Find actions in this source
                </button>
                <button
                  disabled={busy}
                  className="quiet-button text-xs"
                  onClick={() =>
                    run(() =>
                      meetingService.detachSource(eventId, {
                        input_key: source.input_key,
                      }),
                    )
                  }
                >
                  Detach source
                </button>
              </div>
            </article>
          ))}
          {data.analysis.map((job) => (
            <div key={job.id} className="surface-panel p-4 text-sm">
              <p className="font-semibold">Source analysis: {job.status}</p>
              {job.error && <p className="mt-2 text-[#a14d38]">{job.error}</p>}
              {job.result?.questions?.map((question, index) => (
                <p key={index} className="mt-2 text-[#996617]">
                  Needs clarification: {question}
                </p>
              ))}
            </div>
          ))}
        </section>
        {data.proposals.length > 0 && (
          <section className="space-y-3" aria-label="Meeting action proposals">
            <h2 className="text-lg font-bold">Review extracted action items</h2>
            {data.proposals.map((proposal) => (
              <article
                key={proposal.id}
                className="surface-panel space-y-3 p-4"
              >
                <h3 className="font-bold">{proposal.title}</h3>
                <p className="text-sm">
                  {proposal.task.owner || "Owner to confirm"} ·{" "}
                  {time(proposal.task.due_at, data.timezone)} ·{" "}
                  {proposal.task.priority}
                </p>
                <blockquote className="border-l-2 border-[#b9d1c2] pl-3 text-sm italic">
                  {proposal.evidence}
                </blockquote>
                <p className="text-xs text-[#68746d]">
                  From {proposal.source_title}
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={busy}
                    className="primary-button text-xs"
                    onClick={() =>
                      run(() =>
                        agentService.decide(proposal.id, { action: "accept" }),
                      )
                    }
                  >
                    Track proposed action
                  </button>
                  <button
                    className="secondary-button text-xs"
                    onClick={() =>
                      setAction({
                        type: "proposal",
                        item: proposal.task,
                        id: proposal.id,
                      })
                    }
                  >
                    Edit proposed action
                  </button>
                  <button
                    disabled={busy}
                    className="quiet-button text-xs"
                    onClick={() =>
                      run(() =>
                        agentService.decide(proposal.id, { action: "dismiss" }),
                      )
                    }
                  >
                    Dismiss proposed action
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}
        <section className="space-y-3" aria-label="Tracked meeting actions">
          <div className="flex justify-between gap-3">
            <h2 className="text-lg font-bold">
              Tracked commitments · {data.action_total}
            </h2>
            <button
              className="secondary-button text-xs"
              onClick={() => setAction({ type: "new" })}
            >
              Add agreed action
            </button>
          </div>
          {action && (
            <MeetingActionForm
              key={`${action.type}-${action.id || action.item?.id || "new"}`}
              initial={action.item}
              busy={busy}
              close={() => setAction(null)}
              button={
                action.type === "edit" ? "Save action changes" : "Track action"
              }
              save={(values) =>
                run(async () => {
                  if (action.type === "proposal")
                    await agentService.decide(action.id, {
                      action: "accept",
                      task: values,
                    });
                  else if (action.type === "edit")
                    await assistantService.edit(action.item.id, values);
                  else {
                    await meetingService.createAction(
                      eventId,
                      actionRequest({ task: values }),
                    );
                    resetActionRequest();
                  }
                  setAction(null);
                })
              }
            />
          )}
          {!data.actions.length && (
            <p className="surface-panel p-4 text-sm text-[#68746d]">
              No action items on this page. Add an agreed action or accept one
              extracted from your notes.
            </p>
          )}
          {data.actions.map((item) => (
            <ActionCard
              key={item.id}
              item={item}
              timezone={data.timezone}
              busy={busy}
              run={run}
              edit={(item) => setAction({ type: "edit", item })}
            />
          ))}
          <div className="flex gap-2">
            {offset > 0 && (
              <button
                className="secondary-button text-xs"
                onClick={() => setOffset(Math.max(0, offset - 30))}
              >
                Previous actions
              </button>
            )}
            {data.next_offset !== null && (
              <button
                className="secondary-button text-xs"
                onClick={() => setOffset(data.next_offset)}
              >
                More actions
              </button>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
